import { ValidationTrigger, createManagerFactory, formBuilder } from 'app-components/utils/createForm/formBuilder';
import { v4 as uuid } from 'uuid';

import RootState from 'app-components/utils/redux-store/rootState';
import { businessSelectors } from '../business';
import { BUSINESS_REDUCER_KEY } from '../business';
import { DATE_REDUCER_KEY, dateSelectors } from '../date';
import { MILEAGE_TRACKER_REDUCER_KEY, MileageTrackerType, MileageTypeSchema } from './mileageTrackerTypes';

type ReduxState = Pick<
  RootState,
  typeof MILEAGE_TRACKER_REDUCER_KEY | typeof DATE_REDUCER_KEY | typeof BUSINESS_REDUCER_KEY
>;

export const mileageClearState: MileageTrackerType = {
  id: undefined,
  type: 'TRIP',
  clientId: undefined,
  createdAt: undefined,
  modifiedAt: undefined,
  postedOn: undefined,
  vehicleId: 'primary',
  startLocationId: undefined,
  endLocationId: undefined,
  distance: 0,
  value: undefined,
  roundTrip: false,
  businessId: undefined,
  purposeId: undefined,
  notes: undefined,
};

const init = ({ input, store }: { input: Partial<MileageTrackerType>; store: ReduxState }): MileageTrackerType => {
  const postedOnInit = dateSelectors.getCurrentDateString(store);
  const defaultBusiness = businessSelectors.getDefaultBusiness(store)?.id;
  return {
    ...mileageClearState,
    ...input,
    postedOn: input.postedOn ?? postedOnInit,
    businessId: input.businessId ?? defaultBusiness,
  };
};

const mileageTrackerForm = formBuilder({ init })
  .addField('type', {
    selector: (state) => state.type,
    onValueChange: (draft, value: MileageTypeSchema) => {
      draft.type = value;
    },
  })
  .addField('postedOn', {
    selector: (state) => state.postedOn ?? '',
    onValueChange: (draft, value: string) => {
      draft.postedOn = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: ({ postedOn }) => !!postedOn,
        error: 'Required',
      },
    },
  })
  .addField('vehicleId', {
    selector: (state) => state.vehicleId ?? '',
    onValueChange: (draft, value: string) => {
      draft.vehicleId = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: ({ vehicleId }) => !!vehicleId,
        error: 'Required',
      },
    },
  })
  .addField('startLocationId', {
    selector: (state) => state.startLocationId ?? '',
    onValueChange: (draft, value: string) => {
      draft.startLocationId = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: ({ endLocationId, startLocationId }) => (!startLocationId && !endLocationId) || !!startLocationId,
        error: 'Both locations must be provided if one is filled',
      },
    },
  })
  .addField('endLocationId', {
    selector: (state) => state.endLocationId ?? '',
    onValueChange: (draft, value: string) => {
      draft.endLocationId = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: ({ endLocationId, startLocationId }) => (!startLocationId && !endLocationId) || !!endLocationId,
        error: 'Both locations must be provided if one is filled',
      },
    },
  })
  .addField('distance', {
    selector: (state) => state.distance,
    onValueChange: (draft, value: number) => {
      draft.distance = value;
    },
    // validation: { // to bring back later but for now we allow 0 distance
    //   required: {
    //     triggers: ValidationTrigger.onSubmit,
    //     isValid: ({ distance }) => !!distance,
    //     error: 'Required',
    //   },
    // },
  })
  .addField('roundTrip', {
    selector: (state) => state.roundTrip,
    onValueChange: (draft, value: boolean) => {
      draft.roundTrip = value;
    },
  })
  .addField('businessId', {
    selector: (state) => state.businessId ?? '',
    onValueChange: (draft, value: string) => {
      if ((draft.businessId === 'PERSONAL') !== (value === 'PERSONAL')) {
        draft.purposeId = undefined;
      }
      draft.businessId = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: ({ businessId }) => !!businessId,
        error: 'Required',
      },
    },
  })
  .addField('purposeId', {
    selector: (state) => state.purposeId ?? '',
    onValueChange: (draft, value: string) => {
      draft.purposeId = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: ({ purposeId }) => !!purposeId,
        error: 'Trip purpose is required',
      },
    },
  })
  .addField('notes', {
    selector: (state) => state.notes ?? '',
    onValueChange: (draft, value: string) => {
      draft.notes = value;
    },
  })
  .build({
    submitTransform(state): MileageTrackerType {
      return {
        ...state,
        ...(state.type === 'MILES_ONLY'
          ? {
              startLocationId: null,
              endLocationId: null,
              roundTrip: false,
            }
          : undefined),
        ...(state.id ? { id: state.id } : { clientId: uuid().toUpperCase() }),
      };
    },
  });

export const mileageTrackerFormFactory = createManagerFactory(mileageTrackerForm);
